import {z as zod} from "zod";
import {robustClientFetch} from "@vesta/error-handling-remix/fetches/robustClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {
    errorResult,
    dataResult,
    patchResultForCompatibilityIfRequired,
} from "@vesta/error-handling-remix/result";
import {jsonValidator} from "@vesta/global-common-typescript/validators/zodValidators";

// Assumed API returns response in the shape of Result<T>, with a status code of 200
export async function robustJsonResultClientFetch<T>(
    requestUrl: string,
    requestOptions: RequestInit,
    responseValidator: zod.ZodSchema<T>,
): Promise<Result<T>> {
    const responseResult = await robustClientFetch(
        requestUrl,
        requestOptions,
        jsonValidator.pipe(
            zod.discriminatedUnion("success", [
                zod.object({
                    success: zod.literal(true),
                    data: zod.unknown().optional(),
                    ok: zod.unknown().optional(),
                }),
                zod.object({
                    success: zod.literal(false),
                    error: zod.string().optional(),
                    err: zod.string().optional(),
                }),
            ]),
        ) as unknown as zod.ZodSchema<
            | {
                  success: true;
                  data: unknown;
              }
            | {
                  success: true;
                  ok: unknown;
              }
            | {
                  success: false;
                  error: string;
              }
            | {
                  success: false;
                  err: string;
              }
        >,
    );
    if (responseResult.success === false) {
        return responseResult;
    }
    const responseUnpatched = responseResult.data;
    const response = patchResultForCompatibilityIfRequired(responseUnpatched);

    if (response.success === false) {
        return errorResult(new Error(response.error));
    }
    const unvalidatedData = response.data;

    const validatedDataResult = responseValidator.safeParse(unvalidatedData);
    if (validatedDataResult.success === false) {
        console.log("Error communicating with backend: data validation failed");
        console.log("Request URL:", requestUrl);
        console.log("Data:", unvalidatedData);
        console.log("Validation error:", validatedDataResult.error);

        return errorResult(new Error("Error communicating with backend!"));
    }
    const validatedData = validatedDataResult.data;

    return dataResult(validatedData);
}
